import type { ApiErrorEnvelope, ApiValidationIssue } from './envelope'

/** Error codes from uc-core src/common/constants/error-codes.constant.ts that the UI reacts to. */
export const ErrorCodes = {
  BAD_REQUEST: 'ERR_1001',
  VALIDATION_ERROR: 'ERR_1002',
  CONFLICT: 'ERR_1004',
  UNAUTHORIZED: 'ERR_2000',
  INVALID_CREDENTIALS: 'ERR_2001',
  TOKEN_EXPIRED: 'ERR_2002',
  TOKEN_INVALID: 'ERR_2003',
  REFRESH_TOKEN_INVALID: 'ERR_2005',
  ACCOUNT_DISABLED: 'ERR_2008',
  FORBIDDEN: 'ERR_3000',
  INSUFFICIENT_PERMISSIONS: 'ERR_3001',
} as const

export type ApiErrorKind =
  | 'network' // no response at all (offline, DNS, CORS, aborted by timeout)
  | 'timeout'
  | 'unauthorized' // 401 after a refresh attempt
  | 'forbidden' // 403
  | 'not_found' // 404
  | 'conflict' // 409
  | 'validation' // 400 / 422
  | 'rate_limited' // 429
  | 'server' // 5xx
  | 'invalid_response' // body did not match the expected schema
  | 'http' // any other non-2xx

export class ApiError extends Error {
  override readonly name = 'ApiError'
  readonly kind: ApiErrorKind
  readonly status: number
  /** uc-core `errorCode`, e.g. ERR_1001. */
  readonly code?: string
  readonly issues?: ApiValidationIssue[]
  readonly path?: string
  readonly body?: unknown

  constructor(init: {
    kind: ApiErrorKind
    message: string
    status?: number
    code?: string
    issues?: ApiValidationIssue[]
    path?: string
    body?: unknown
    cause?: unknown
  }) {
    super(init.message, { cause: init.cause })
    this.kind = init.kind
    this.status = init.status ?? 0
    this.code = init.code
    this.issues = init.issues
    this.path = init.path
    this.body = init.body
  }

  /** True when retrying later may succeed without anyone changing anything. */
  get isTransient(): boolean {
    return (
      this.kind === 'network' ||
      this.kind === 'timeout' ||
      this.kind === 'server' ||
      this.kind === 'rate_limited' ||
      this.status === 408
    )
  }

  static fromResponse(status: number, body: unknown, path: string): ApiError {
    const env = (typeof body === 'object' && body !== null ? body : {}) as Partial<ApiErrorEnvelope>
    const message =
      typeof env.message === 'string' && env.message
        ? env.message
        : `Request failed with status ${status}`
    const issues = Array.isArray(env.errors) ? (env.errors as ApiValidationIssue[]) : undefined
    return new ApiError({
      kind: kindForStatus(status),
      status,
      message,
      code: typeof env.errorCode === 'string' ? env.errorCode : undefined,
      issues,
      path,
      body,
    })
  }
}

function kindForStatus(status: number): ApiErrorKind {
  if (status === 400 || status === 422) return 'validation'
  if (status === 401) return 'unauthorized'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'not_found'
  if (status === 409) return 'conflict'
  if (status === 429) return 'rate_limited'
  if (status === 408) return 'timeout'
  if (status >= 500) return 'server'
  return 'http'
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError
}
